import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import i18next from 'i18next';
import { memo, useId } from 'react';

import {
  MultiStoreCenter,
  MultiStoreCenterDay,
} from '../../../../interface/multi-store';
import { getHourOptions } from '../../../../utils/multi-store/availability';
import { MultiStoreBusyCard } from './multi-store-busy-card';
import { MultiStoreFreeCards } from './multi-store-free-cards';
import { MultiStoreStatusCard } from './multi-store-status-card';
import { useDistanceReference } from './use-distance-reference';

interface MultiStoreHourAvailabilityProps {
  // orario scelto; senza, la sezione non compare
  hour?: string;
  day?: string;
  // centri inclusi (origin e aggiunti) e quelli di cui sono arrivati gli orari del giorno
  included: MultiStoreCenter[];
  centers: MultiStoreCenterDay[];
  unavailableIds: string[];
  // centro corrente raggiunto passando a "qualsiasi operatore": la sua scheda lo ricorda
  anyUserCenterId?: string;
  onSwitch: (center: MultiStoreCenterDay, hour: string) => void;
}

// Sotto gli orari: dove è libero l'orario scelto. Prima i centri liberi (il corrente, attivo,
// poi per distanza), da scegliere come radio; poi gli altri centri con gli orari più vicini,
// quelli ancora in caricamento e quelli non disponibili.
export const MultiStoreHourAvailability = memo(
  function MultiStoreHourAvailability({
    hour,
    day,
    included,
    centers,
    unavailableIds,
    anyUserCenterId,
    onSwitch,
  }: MultiStoreHourAvailabilityProps): JSX.Element | null {
    const titleId = useId();
    const distanceReference = useDistanceReference('distances');

    if (hour === undefined) return null;

    const options = getHourOptions(included, centers, unavailableIds, hour);
    const hasOthers =
      options.busy.length > 0 ||
      options.loading.length > 0 ||
      options.unavailable.length > 0;
    // dayjs con la lingua italiana caricata in App.tsx, come il titolo del giorno: con moment
    // il giorno usciva in inglese («Thursday 8 October») nonostante l'import della lingua
    const dayLabel =
      day === undefined ? '' : dayjs(day).locale('it').format('dddd D MMMM');

    return (
      <div className="multi-store-availability">
        <div className="multi-store-heading">
          <Typography variant="contentTitle2" id={titleId}>
            {i18next.t('pages.when.multi_store.where_title', { hour })}
          </Typography>
          <Typography variant="contentDescription2">
            {distanceReference}
          </Typography>
        </div>
        {options.free.length > 0 && (
          <MultiStoreFreeCards
            titleId={titleId}
            centers={options.free}
            hour={hour}
            anyUserCenterId={anyUserCenterId}
            onSwitch={onSwitch}
          />
        )}
        {hasOthers && (
          <div className="multi-store-cards">
            {options.busy.map((busy) => (
              <MultiStoreBusyCard
                key={busy.center.pointOfSale.id}
                busy={busy}
                hour={hour}
                dayLabel={dayLabel}
                onSwitch={onSwitch}
              />
            ))}
            {options.loading.map((center) => (
              <MultiStoreStatusCard
                key={center.pointOfSale.id}
                center={center}
                isLoading
              />
            ))}
            {options.unavailable.map((center) => (
              <MultiStoreStatusCard
                key={center.pointOfSale.id}
                center={center}
                isLoading={false}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
);
